import React, { useState, useEffect } from "react";
import {
  Stack,
  TextInput,
  Avatar,
  Group,
  Text,
  Badge,
  //FileInput,
  Box,
  Alert,
} from "@mantine/core";
import {
  IconUser,
  IconCheck,
  IconAlertCircle,
  /*   IconUpload,
  IconX, */
} from "@tabler/icons-react";
import { User, UserInput } from "../../../utils";
import { AppButton } from "@/components/generic";

export interface UserProfileFormProps {
  /** User data to populate the form */
  user: User | null;

  /** Whether the form is in loading state */
  loading?: boolean;

  /** Whether the form is being updated */
  updating?: boolean;

  /** Callback when form is submitted */
  onSubmit?: (data: UserInput) => void;

  /** Callback when email confirmation is requested */
  onRequestEmailConfirmation?: () => void;

  /** Error message to display */
  error?: string | null;

  /** Success message to display */
  success?: string | null;
}

export const UserProfileForm: React.FC<UserProfileFormProps> = ({
  user,
  loading = false,
  updating = false,
  onSubmit,
  //onRequestEmailConfirmation,
  error,
  success,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || "");
      setLastName(user.lastName || "");
      setAvatarUrl(user.avatarUrl || "");
      setPreviewUrl(null);
      setAvatarFile(null);
    }
  }, [user]);

  // Handle avatar file selection
  useEffect(() => {
    if (avatarFile) {
      const url = URL.createObjectURL(avatarFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }

    return;
  }, [avatarFile]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!onSubmit) return;

    const formData: UserInput = {
      firstName: firstName.trim() || undefined,
      lastName: lastName.trim() || undefined,
      avatarUrl: previewUrl || avatarUrl || undefined,
    };

    // TODO: Handle file upload if avatarFile exists
    // For now, we'll just use the URL

    onSubmit(formData);
  };

  /*   const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setPreviewUrl(null);
    setAvatarUrl("");
  }; */

  const getAvatarSrc = () => {
    if (previewUrl) return previewUrl;
    if (avatarUrl) return avatarUrl;
    return null;
  };

  const hasChanges = () => {
    if (!user) return false;

    return (
      firstName !== (user.firstName || "") ||
      lastName !== (user.lastName || "") ||
      avatarFile !== null ||
      avatarUrl !== (user.avatarUrl || "")
    );
  };

  if (loading) {
    return (
      <Stack gap="md">
        <Text>Loading user profile...</Text>
      </Stack>
    );
  }

  if (!user) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red">
        User data not available
      </Alert>
    );
  }

  console.log("== user", user);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack gap="lg">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {error}
          </Alert>
        )}

        {success && (
          <Alert icon={<IconCheck size={16} />} color="green">
            {success}
          </Alert>
        )}

        {/* Avatar Section */}
        <Stack gap="sm" align="center">
          <Group gap="md" align="center">
            <Avatar src={getAvatarSrc()} size={80} radius="md">
              <IconUser size={40} />
            </Avatar>

            {/* <Stack gap="xs">
              <FileInput
                placeholder="Choose image"
                accept="image/*"
                value={avatarFile}
                onChange={setAvatarFile}
                leftSection={<IconUpload size={16} />}
                size="sm"
                disabled={updating}
              />

              {(avatarUrl || previewUrl) && (
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  leftSection={<IconX size={14} />}
                  onClick={handleRemoveAvatar}
                  disabled={updating}
                >
                  Remove
                </Button>
              )}
            </Stack> */}
          </Group>
        </Stack>

        {/* Personal Information */}
        <Stack gap="md">
          <Text size="sm" fw={500}>
            Personal Information
          </Text>

          <Group grow>
            <TextInput
              label="First Name"
              placeholder="Enter your first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={updating}
              required
              size="md"
            />

            <TextInput
              label="Last Name"
              placeholder="Enter your last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={updating}
              required
              size="md"
            />
          </Group>
        </Stack>

        {/* Account Information (Read-only) */}
        <Stack gap="md">
          <Text size="sm" fw={500}>
            Account Information
          </Text>

          <TextInput
            label="Username"
            value={user.username || ""}
            readOnly
            variant="filled"
            size="md"
          />

          <TextInput
            label="User Role"
            value={user.userRole?.roleName || "N/A"}
            readOnly
            variant="filled"
            size="md"
          />

          <Group>
            <TextInput
              label="Email"
              value={user.email || ""}
              readOnly
              variant="filled"
              style={{ flex: 1 }}
              rightSection={
                user.emailConfirmation ? (
                  <Badge color="green" size="xs" variant="light">
                    <Group gap={4}>
                      <IconCheck size={12} />
                      <Text size="xs">Verified</Text>
                    </Group>
                  </Badge>
                ) : undefined
              }
              size="md"
            />

            {/* {!user.emailConfirmation && (
              <Button
                variant="light"
                size="sm"
                onClick={onRequestEmailConfirmation}
                disabled={updating}
                style={{ alignSelf: "flex-end" }}
              >
                Confirm Email
              </Button>
            )} */}
          </Group>
        </Stack>

        {/* Submit Button */}
        <Group justify="flex-end" mt="lg">
          <AppButton
            type="submit"
            loading={updating}
            disabled={!hasChanges() || updating}
          >
            Save Changes
          </AppButton>
        </Group>
      </Stack>
    </Box>
  );
};
